// @ts-nocheck
import React, { useEffect, useState } from 'react';
import fetchWithAuth from '../utils/fetchWithAuth.ts';
import DayCard from '../components/MealPlanner/DayCard.tsx';
import InlineRecipePicker from '../components/MealPlanner/InlineRecipePicker.tsx';
import SelectedRecipePreview from '../components/MealPlanner/SelectedRecipePreview.tsx';
import BottomSheet from '../components/Shared/BottomSheet';
import { getCurrentWeekStart } from '../utils/getCurrentWeekStart.ts';
import { WeekNav } from '../components/Shared/WeekNav.tsx';

const MealPlanner = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const initialWeekStart = getCurrentWeekStart();
  const todayIndex = Math.floor((today - initialWeekStart) / 86400000);

  const [weekStart, setWeekStart] = useState(initialWeekStart);

  // now an array of 7 arrays (one per day)
  const [mealPlan, setMealPlan] = useState<Array<any>[]>(() =>
    Array.from({ length: 7 }, () => [])
  );

  const [pickerDay, setPickerDay] = useState<number | null>(null);
  // track which tab is active in the picker (0: Yours, 1: Saved, 2: Search)
  const [pickerTab, setPickerTab] = useState<number>(0);
  const [selectedRecipePreview, setSelectedRecipePreview] = useState<{
    recipe: any;
    dayIndex: number;
  } | null>(null);

  // Helper to fetch & re-index the meal plan
  const fetchAndSetMealPlan = async () => {
    const res = await fetchWithAuth(`/api/meal-plan?weekStart=${isoWeekStart}`);
    const plan = await res;
    // Build an array-of-7, then merge any duplicate recipeIds for each day
    const structured: any[][] = Array.from({ length: 7 }, () => []);
    for (const entry of plan.entries || []) {
      const dayIndex = entry.day;
      // Try to find an existing record for the same recipeId
      const existing = structured[dayIndex].find(
        (e) => e.recipeId === entry.recipe.id
      );

      if (existing) {
        // Just bump servings
        existing.servings += entry.servings;
      } else {
        // First time we see this recipe on that day
        structured[dayIndex].push({
          id: entry.id,
          recipeId: entry.recipe.id,
          title: entry.recipe.title,
          imageUrl: entry.recipe.imageUrl,
          servings: entry.servings,
          description: entry.recipe.description,
          tags: entry.recipe.recipeTags.map((rt: any) => rt.tag.name),
        });
      }
    }
    setMealPlan(structured);
  };

  // on click “Remove” in DayCard
  const handleRemoveEntry = async (entryId: string) => {
    await fetchWithAuth(`/api/meal-plan/${entryId}`, { method: 'DELETE' });
    await fetchAndSetMealPlan();
  };

  // on click a recipe card to edit
  const handleRecipePreview = (entryId: string, dayIndex: number) => {
    const entry = mealPlan[dayIndex].find((e) => e.id === entryId);
    if (!entry) return;
    setSelectedRecipePreview({ recipe: entry, dayIndex });
  };

  const isoWeekStart = weekStart.toISOString();

  // Fetch whenever weekStart changes
  useEffect(() => {
    fetchAndSetMealPlan();
  }, [isoWeekStart]);

  // useEffect(() => {
  //   setSelectedRecipePreview(null);
  // }, [selectedDayIndex, isoWeekStart]);

  const goToWeek = (offset) => {
    // next tick, actually shift the week (so exit uses that new direction)
    setTimeout(() => {
      // shift the week, clear any open sheets
      setPickerDay(null);
      setSelectedRecipePreview(null);
      setWeekStart((ws) => {
        const newStart = new Date(ws);
        newStart.setDate(newStart.getDate() + offset * 7);
        return newStart;
      });
      // reset picker and preview
      setPickerDay(null);
      setSelectedRecipePreview(null);
    }, 0);
  };

  const goToThisWeek = () => {
    const current = getCurrentWeekStart();
    // reset to “this week”
    setPickerDay(null);
    setSelectedRecipePreview(null);
    setWeekStart(current);
  };

  // compute the nav label
  const currentStart = getCurrentWeekStart();
  const diffMs = weekStart.getTime() - currentStart.getTime();
  const diffWeeks = Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
  const formatDDMM = (d: Date) =>
    `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
  let navLabel: string;
  if (diffWeeks === 0) navLabel = 'This Week';
  else if (diffWeeks === 1) navLabel = 'Next Week';
  else if (diffWeeks === -1) navLabel = 'Previous Week';
  else {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    navLabel = `${formatDDMM(weekStart)} - ${formatDDMM(end)}`;
  }

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* ── Fixed WeekNav header ── */}
      <div
        className="fixed inset-x-0 z-50 bg-white h-[72px] px-4 flex items-center justify-center gap-3"
        style={{ top: 'var(--safe-top)' }}
      > 
        <WeekNav weekStart={weekStart} onChangeWeek={goToWeek} />
      </div>

      {/* ── Scrolling content ── */}
      <div
        className="pb-[140px] px-4 max-w-md mx-auto"
        style={{ paddingTop: 'calc(max(env(safe-area-inset-top, 0px), 20px) + 52px)' }}
      >
        {/* Days stacked with full-border + separators */}
        <div className="border-b border-gray-200 divide-y divide-gray-200">
          {days.map((day, idx) => (
            <DayCard
              key={idx}
              dateISO={day.toISOString()}
              entries={mealPlan[idx]}
              onAdd={() => setPickerDay(idx)}
              onClickEntry={(id) => handleRecipePreview(id, idx)}
              onRemoveEntry={(id) => handleRemoveEntry(id)}
            />
          ))}
        </div>

        <BottomSheet
          isOpen={pickerDay !== null}
          onClose={() => {
            setPickerDay(null);
            setPickerTab(0);
          }}
          fullScreen={pickerTab === 2}
          height="90vh"
        >
          <InlineRecipePicker
            dayIndex={pickerDay!}
            weekStart={weekStart}
            tab={pickerTab}
            setTab={setPickerTab}
            onClose={() => {
              setPickerDay(null);
              setPickerTab(0);
            }}
            onAdded={async () => {
              await fetchAndSetMealPlan();
              setPickerDay(null);
              setPickerTab(0);
            }}
          />
        </BottomSheet>

        <BottomSheet
          isOpen={Boolean(selectedRecipePreview)}
          onClose={() => setSelectedRecipePreview(null)}
        >
          {selectedRecipePreview && (
            <SelectedRecipePreview
              recipe={selectedRecipePreview.recipe}
              servings={selectedRecipePreview.recipe.servings}
              dayIndex={selectedRecipePreview.dayIndex}
              weekStartISO={isoWeekStart}
              onClose={() => setSelectedRecipePreview(null)}
              onSaved={async () => {
                await fetchAndSetMealPlan();
                setSelectedRecipePreview(null);
              }}
            />
          )}
        </BottomSheet>
      </div>
    </div>
  );
};

export default MealPlanner;
